/**
 * The FluidDream mark.
 *
 * The drop path is the ecosystem's, byte-identical to FluidTalk's BrandMark and
 * FluidCloud's Logo — do not redraw it. What makes each product its own is the
 * gradient and the white glyph inside: FluidTalk's three dots, FluidCloud's
 * cloud, FluidDream's star.
 *
 * The star is one shape, not a cluster, and spans ~28% of the mark's width —
 * the same share the cloud and the dots occupy. That's what keeps it legible at
 * favicon size, where three small shapes turned to mush.
 *
 * The gradient id is uniquified per instance so several marks on one page can't
 * collide.
 */
import { useId } from 'react';

export interface BrandMarkProps {
  /** Rendered box size in px. The mark is square. */
  size?: number;
  className?: string;
  /** The signature ember drop-shadow. */
  glow?: boolean;
}

const DROP = 'M32 5C32 5 13 27 13 41a19 19 0 1 0 38 0C51 27 32 5 32 5Z';

/** Ten vertices, outer r 9, inner r 3.8, centred on the bowl — chunky enough to hold at 15px. */
const STAR =
  'M32,32.5 L34.23,38.43 L40.56,38.72 L35.61,42.67 L37.29,48.78 L32,45.3 L26.71,48.78 L28.39,42.67 L23.44,38.72 L29.77,38.43 Z';

export default function BrandMark({ size = 28, className, glow = true }: BrandMarkProps) {
  const id = useId();
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      className={className}
      role="img"
      aria-label="FluidDream"
      style={glow ? { filter: 'drop-shadow(0 2px 9px rgba(239,113,52,.35))', flex: 'none' } : { flex: 'none' }}
    >
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ffb066" />
          <stop offset="1" stopColor="#e05c1f" />
        </linearGradient>
      </defs>
      <path d={DROP} fill={`url(#${id})`} />
      <path d={STAR} fill="#ffffff" />
    </svg>
  );
}

/** The wordmark lockup, in the ecosystem's two-tone uppercase style. */
export function Wordmark({ size = 20 }: { size?: number }) {
  return (
    <span
      style={{
        fontFamily: 'var(--fd-display)',
        fontSize: size,
        fontWeight: 700,
        letterSpacing: '-0.03em',
        color: '#fff',
        whiteSpace: 'nowrap',
      }}
    >
      FLUID<span style={{ color: 'var(--fd-primary)' }}>DREAM</span>
    </span>
  );
}
